import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useActiveOrgStore } from "@/stores/active-org";
import { isChunkLoadError, markChunkReload, clearChunkReload } from "@/utils/chunk-reload";

declare module "vue-router" {
  interface RouteMeta {
    requiresAuth?: boolean;
    public?: boolean;
    // string = exige a permissão; string[] = exige QUALQUER uma (anyOf).
    requiresPermission?: string | string[];
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/pages/LoginPage.vue"),
    meta: { public: true },
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: () => import("@/pages/ForgotPasswordPage.vue"),
    meta: { public: true },
  },
  {
    path: "/reset-password/:token",
    name: "reset-password",
    component: () => import("@/pages/ResetPasswordPage.vue"),
    meta: { public: true },
    props: true,
  },
  {
    path: "/accept-invite/:token",
    name: "accept-invite",
    component: () => import("@/pages/AcceptInvitePage.vue"),
    meta: { public: true },
    props: true,
  },
  {
    path: "/2fa-challenge",
    name: "2fa-challenge",
    component: () => import("@/pages/TwoFactorChallengePage.vue"),
    meta: { public: true },
  },
  {
    path: "/",
    component: () => import("@/layouts/AppLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: { name: "dashboard" } },
      { path: "dashboard", name: "dashboard", component: () => import("@/pages/DashboardPage.vue") },
      { path: "alerts", name: "alerts", component: () => import("@/pages/AlertsPage.vue") },
      { path: "me", name: "me", component: () => import("@/pages/MePage.vue") },
      {
        path: "me/sessions",
        name: "me-sessions",
        component: () => import("@/pages/MeSessionsPage.vue"),
      },
      {
        path: "admin/organizations",
        name: "admin-organizations",
        component: () => import("@/pages/AdminOrganizationsPage.vue"),
        meta: { requiresPermission: "org:manage_all" },
      },
      {
        path: "system",
        name: "system",
        component: () => import("@/pages/SystemSettingsPage.vue"),
        // API Keys virou aba aqui → api_key:manage também entra (anyOf preserva
        // o acesso de quem só gerencia chaves, sem ver a aba de identidade).
        meta: { requiresPermission: ["org:manage_all", "api_key:manage", "system_log:view"] },
      },
      {
        path: "settings/organization",
        name: "settings-organization",
        component: () => import("@/pages/SettingsOrganizationPage.vue"),
        // Gamificação/Limites/Classificação saíram para "Atendimento".
        meta: { requiresPermission: "tenant:admin" },
      },
      // Taxonomias/Classificações agora é a aba "Classificação" de Atendimento.
      {
        path: "settings/taxonomies",
        redirect: { name: "escala", query: { tab: "classificacoes" } },
      },
      {
        path: "settings/users",
        name: "settings-users",
        component: () => import("@/pages/SettingsUsersPage.vue"),
        // Convites e Papéis viraram abas aqui → anyOf preserva o acesso.
        meta: { requiresPermission: ["user:read", "user:write", "role:read"] },
      },
      // Convites e Papéis agora são abas de Usuários.
      {
        path: "settings/invitations",
        redirect: { name: "settings-users", query: { tab: "convites" } },
      },
      {
        path: "settings/roles",
        redirect: { name: "settings-users", query: { tab: "papeis" } },
      },
      // API Keys agora é a aba "API Keys" da página Sistema.
      {
        path: "settings/api-keys",
        redirect: { name: "system", query: { tab: "apikeys" } },
      },
      {
        path: "inbox",
        name: "inbox",
        component: () => import("@/pages/InboxPage.vue"),
        meta: { requiresPermission: "conversation:read" },
      },
      {
        path: "meu-desempenho",
        name: "my-performance",
        component: () => import("@/pages/MyPerformancePage.vue"),
        meta: { requiresPermission: "conversation:read" },
      },
      {
        path: "monitoramento",
        name: "monitoring",
        component: () => import("@/pages/MonitoringPage.vue"),
        meta: { requiresPermission: "conversation:supervise" },
      },
      {
        path: "escala",
        name: "escala",
        component: () => import("@/pages/AtendimentoPage.vue"),
        // Abas: Escala/Fila (availability:WRITE — gestão), Macros (macro:write),
        // Gamificação/Limites/Status de ausência (tenant:admin), Classificação
        // (conversation_tag:write), Bloqueios (blacklist:write).
        // availability:read (papel agente) NÃO abre a área — ver AtendimentoPage.
        meta: { requiresPermission: ["availability:write", "tenant:admin", "conversation_tag:write", "macro:write", "blacklist:write"] },
      },
      {
        path: "customers",
        name: "customers",
        component: () => import("@/pages/CustomersListPage.vue"),
        meta: { requiresPermission: "customer:read" },
      },
      {
        path: "customers/:id",
        name: "customer-detail",
        component: () => import("@/pages/CustomerDetailPage.vue"),
        meta: { requiresPermission: "customer:read" },
        props: true,
      },
      {
        path: "settings/services",
        name: "settings-services",
        component: () => import("@/pages/SettingsServicesPage.vue"),
        meta: { requiresPermission: "service:read" },
      },
      {
        path: "settings/services/:id",
        name: "settings-service-detail",
        component: () => import("@/pages/SettingsServiceDetailPage.vue"),
        meta: { requiresPermission: "service:read" },
        props: true,
      },
      {
        path: "settings/bots",
        name: "settings-bots",
        component: () => import("@/pages/SettingsBotsPage.vue"),
        // Tools/Comandos viraram abas aqui → anyOf preserva o acesso de quem só
        // tem tool:read (Tools) ou command:write (Comandos).
        meta: { requiresPermission: ["bot:read", "tool:read", "command:write"] },
      },
      {
        path: "settings/bots/:id",
        name: "settings-bot-detail",
        component: () => import("@/pages/SettingsBotDetailPage.vue"),
        meta: { requiresPermission: "bot:read" },
        props: true,
      },
      {
        path: "settings/commands/:id",
        name: "settings-command-detail",
        component: () => import("@/pages/SettingsCommandDetailPage.vue"),
        meta: { requiresPermission: "command:write" },
        props: true,
      },
      // Tools agora é a aba "Tools" de Bots.
      {
        path: "settings/tools",
        redirect: { name: "settings-bots", query: { tab: "tools" } },
      },
      {
        path: "settings/credentials",
        name: "settings-credentials",
        component: () => import("@/pages/SettingsCredentialsPage.vue"),
        meta: { requiresPermission: "credentials:read" },
      },
      {
        path: "settings/analytics",
        name: "settings-analytics",
        component: () => import("@/pages/SettingsAnalyticsPage.vue"),
        // anyOf: guarda-chuva OU qualquer aba granular. A página esconde as abas
        // que o usuário não pode ver (ver analyticsTabs.ts).
        meta: {
          requiresPermission: [
            "analytics:read",
            "analytics:overview",
            "analytics:tags",
            "analytics:agents",
            "analytics:bot",
            "analytics:service",
            "analytics:satisfaction",
            "analytics:ai",
          ],
        },
      },
      {
        path: "settings/costs",
        name: "settings-costs",
        component: () => import("@/pages/SettingsCostsPage.vue"),
        meta: { requiresPermission: "tenant:admin" },
      },
      // /settings/channels was retired — channel management lives inside
      // the Service detail page now. Redirect any inbound link so old
      // bookmarks / docs land somewhere sensible.
      {
        path: "settings/channels",
        redirect: { name: "settings-services" },
      },
      {
        path: "audit",
        name: "audit",
        component: () => import("@/pages/AuditPage.vue"),
        meta: { requiresPermission: "audit:view" },
      },
      {
        path: "forbidden",
        name: "forbidden",
        component: () => import("@/pages/ForbiddenPage.vue"),
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    component: () => import("@/pages/NotFoundPage.vue"),
    meta: { public: true },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.bootstrapped) {
    await auth.hydrate();
    // Restaura a ORG ATIVA (superadmin) do localStorage AQUI — antes de qualquer
    // página montar. Se deixado para o onMounted do AppLayout, o onMounted das
    // páginas-filho roda ANTES (ordem filho→pai no Vue), então stores como a de
    // credenciais fariam load() com activeID ainda nulo → escopo errado (org
    // home em vez da ativa). Hidratar no guard garante activeID + X-Org-Id antes.
    useActiveOrgStore().hydrate();
  }
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: "login", query: { redirect: to.fullPath } };
  }
  if (to.name === "login" && auth.isAuthenticated) {
    return { name: "dashboard" };
  }
  const need = to.meta.requiresPermission;
  if (need) {
    const ok = Array.isArray(need)
      ? need.some((p) => auth.hasPermission(p))
      : auth.hasPermission(need);
    if (!ok) return { name: "forbidden" };
  }
  return true;
});

// Navegação concluída ⇒ rearma a guarda de reload por chunk stale.
router.afterEach(() => clearChunkReload());

// Após um deploy, navegar para uma rota cujo chunk (lazy import) tem hash antigo
// falha — o menu "trava". Recarregamos na rota pretendida (= o F5 manual),
// uma vez (guarda anti-loop até a próxima navegação OK).
router.onError((err, to) => {
  if (isChunkLoadError(err) && markChunkReload()) {
    window.location.assign(to?.fullPath ?? window.location.pathname);
  }
});
